import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, Drawer, CircularProgress } from '@mui/material';

import { getAcl, getMenuAccess } from '../../../utils/common';
import useResponsive from '../../../hooks/useResponsive';
import logger from '../../../utils/logger';

import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';

import navConfig from './config';

const NAV_WIDTH = 280;

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { pathname } = useLocation();
  const [acl, setAcl] = useState(null);
  const [isLoadingAcl, setIsLoadingAcl] = useState(true);
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  // Load ACL data on component mount and when it changes
  useEffect(() => {
    const loadAclData = async () => {
      try {
        setIsLoadingAcl(true);

        // First try to get existing ACL data
        let aclData = getAcl();

        // If no ACL data exists, fetch it
        if (!aclData || aclData === '[]' || aclData === 'null') {
          await getMenuAccess(true); // Force refresh
          aclData = getAcl();
        }

        // Parse and set ACL data
        if (aclData && aclData !== '[]' && aclData !== 'null') {
          const parsedAcl = JSON.parse(aclData);
          setAcl(parsedAcl);
        } else {
          logger.warn('⚠️ Nav: Still no ACL data available after fetch attempt');
          // Set a default ACL with basic permissions to prevent menu from being empty
          const defaultAcl = [
            { nama_menu: 'dashboard', read: true },
            { nama_menu: 'data_set', read: true },
            { nama_menu: 'alert', read: true },
            { nama_menu: 'calendar', read: true },
            { nama_menu: 'information', read: true },
            { nama_menu: 'report', read: true },
            { nama_menu: 'action', read: true },
            { nama_menu: 'user', read: true },
            { nama_menu: 'activity', read: true },
            { nama_menu: 'about', read: true },
          ];
          setAcl(defaultAcl);
        }
      } catch (error) {
        logger.error('❌ Nav: Error loading ACL data:', error);
        // Set default ACL on error to prevent empty menu
        const defaultAcl = [
          { nama_menu: 'dashboard', read: true },
          { nama_menu: 'data_set', read: true },
          { nama_menu: 'alert', read: true },
          { nama_menu: 'calendar', read: true },
          { nama_menu: 'information', read: true },
          { nama_menu: 'report', read: true },
          { nama_menu: 'action', read: true },
          { nama_menu: 'user', read: true },
          { nama_menu: 'activity', read: true },
          { nama_menu: 'about', read: true },
        ];
        setAcl(defaultAcl);
      } finally {
        setIsLoadingAcl(false);
      }
    };

    loadAclData();

    // Listen for storage changes to update ACL when it's refreshed
    const handleStorageChange = (e) => {
      if (e.key === 'acl' && e.newValue) {
        try {
          setAcl(JSON.parse(e.newValue));
        } catch (error) {
          logger.error('Error parsing updated ACL data:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom ACL update events
    const handleAclUpdate = () => {
      const aclData = getAcl();
      if (aclData && aclData !== '[]' && aclData !== 'null') {
        try {
          setAcl(JSON.parse(aclData));
        } catch (error) {
          logger.error('Error parsing ACL data on update:', error);
        }
      }
    };

    window.addEventListener('aclUpdated', handleAclUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('aclUpdated', handleAclUpdate);
    };
  }, []);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      {isLoadingAcl ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <NavSection
          data={navConfig.filter((c) => {
            const menu = acl?.find((e) => c.namaroute === e.nama_menu && e.read === true);
            return menu !== undefined || c.namaroute === 'logout';
          })}
        />
      )}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
